import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "Our Story" },
  { to: "/founders", label: "Founders" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Our Work" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img src="/logo.png" alt="Legacy Lawn & Landscape" className="h-12 sm:h-14 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-500 tracking-wide transition-colors hover:text-emerald ${
                  isActive ? "text-emerald" : "text-charcoal/80"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-btn"
                className="text-sm font-500 text-charcoal/80 hover:text-emerald transition-colors"
              >
                Portal
              </Link>
              <button
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="inline-flex items-center gap-1.5 text-sm font-500 text-charcoal/70 hover:text-emerald"
              >
                <LogOut className="w-4 h-4" /> Log out
              </button>
            </>
          ) : (
            <Link
              to="/portal"
              data-testid="nav-portal-btn"
              className="text-sm font-500 text-charcoal/80 hover:text-emerald transition-colors"
            >
              Customer Portal
            </Link>
          )}
          <Link
            to="/quote"
            data-testid="nav-quote-btn"
            className="bg-emerald text-white px-5 py-2.5 rounded-sm text-sm font-600 tracking-wide hover:bg-emerald-dark transition-colors"
          >
            Request a Quote
          </Link>
        </div>

        <button
          className="lg:hidden text-charcoal"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-white px-6 py-4 flex flex-col gap-3" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="text-base font-500 text-charcoal py-1"
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/portal" onClick={() => setOpen(false)} className="text-base font-500 text-charcoal py-1">
            Customer Portal
          </Link>
          <Link
            to="/quote"
            onClick={() => setOpen(false)}
            className="bg-emerald text-white text-center px-5 py-3 rounded-sm font-600 mt-2"
          >
            Request a Quote
          </Link>
        </div>
      )}
    </header>
  );
}
